import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Fingerprint, Lock, Delete } from "lucide-react";
import { useApp } from "../context/AppContext";
import ScreenHeader from "../components/ScreenHeader";
import PartnerBadge from "../components/PartnerBadge";

export const AuthScreen = () => {
  const nav = useNavigate();
  const { activeTx, partner, updateActiveTx } = useApp();
  const [mode, setMode] = useState("otp"); // otp | bio
  const [otp, setOtp] = useState("");
  const [bioState, setBioState] = useState("idle"); // idle | scanning | approved
  const [error, setError] = useState("");
  const holdTimer = useRef(null);

  useEffect(() => {
    if (!activeTx) {
      nav("/", { replace: true });
    }
  }, [activeTx, nav]);

  useEffect(() => {
    if (otp.length === 4) {
      // any 4 digits accepted
      setTimeout(() => {
        updateActiveTx({ status: "AUTH_PENDING" });
        nav("/send/processing");
      }, 250);
    }
  }, [otp, nav, updateActiveTx]);

  const handleKey = (k) => {
    setError("");
    if (k === "back") setOtp((o) => o.slice(0, -1));
    else if (otp.length < 4) setOtp((o) => o + k);
  };

  const startBio = () => {
    setBioState("scanning");
    holdTimer.current = setTimeout(() => {
      setBioState("approved");
      setTimeout(() => {
        updateActiveTx({ status: "AUTH_PENDING" });
        nav("/send/processing");
      }, 450);
    }, 1200);
  };

  const cancelBio = () => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    if (bioState !== "approved") setBioState("idle");
  };

  return (
    <motion.div
      initial={{ x: 20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -20, opacity: 0 }}
      transition={{ type: "spring", bounce: 0, duration: 0.35 }}
      className="h-full flex flex-col"
      data-testid="auth-screen"
    >
      <ScreenHeader title="Authenticate" testId="auth-back-btn" />

      <div className="px-6">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-gray-900 dark:text-white" strokeWidth={2.5} />
          <h1 className="font-display text-[24px] font-bold tracking-tight dark:text-white">
            Approve your payment
          </h1>
        </div>
        <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1.5 leading-snug">
          Secure approval required before {partner.name} executes the transfer.
        </p>

        {/* Mode toggle */}
        <div className="mt-5 bg-gray-100 dark:bg-white/10 rounded-full p-1 flex">
          <button
            data-testid="auth-mode-otp"
            onClick={() => setMode("otp")}
            className={
              "flex-1 h-10 rounded-full text-[13px] font-semibold transition-all " +
              (mode === "otp" ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm" : "text-gray-500 dark:text-gray-400")
            }
          >
            PIN / OTP
          </button>
          <button
            data-testid="auth-mode-bio"
            onClick={() => setMode("bio")}
            className={
              "flex-1 h-10 rounded-full text-[13px] font-semibold transition-all " +
              (mode === "bio" ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm" : "text-gray-500 dark:text-gray-400")
            }
          >
            Biometric
          </button>
        </div>
      </div>

      {mode === "otp" ? (
        <div className="flex-1 flex flex-col">
          <div className="flex-1 flex flex-col items-center justify-center px-6">
            <div className="text-[11px] uppercase tracking-widest text-gray-400 font-bold mb-4">
              Enter 4-digit code
            </div>
            <div className="flex gap-3 mb-3">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  data-testid={`otp-slot-${i}`}
                  className={
                    "w-12 h-14 rounded-2xl border-2 flex items-center justify-center font-display text-2xl font-bold transition-all " +
                    (otp[i]
                      ? "border-gray-900 dark:border-white bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                      : "border-gray-200 dark:border-white/10 text-gray-900 dark:text-white")
                  }
                >
                  {otp[i] ? "•" : ""}
                </div>
              ))}
            </div>
            <div className="text-[11px] text-gray-400">Any 4 digits (demo)</div>
            {error && (
              <div className="text-[12px] text-red-500 mt-2">{error}</div>
            )}
          </div>

          <div className="px-6 pb-6">
            <div className="flex justify-center mb-4">
               <PartnerBadge partner={partner.name} variant="pill" />
            </div>
            <div className="grid grid-cols-3 gap-1">
              {["1","2","3","4","5","6","7","8","9","","0","back"].map((k, idx) => {
                if (k === "") return <div key={idx} />;
                return (
                  <button
                    key={idx}
                    data-testid={`otp-key-${k}`}
                    onClick={() => handleKey(k)}
                    className="dialpad-key h-14 rounded-xl flex items-center justify-center text-[22px] font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition-colors"
                  >
                    {k === "back" ? <Delete className="w-5 h-5 text-gray-900 dark:text-white" strokeWidth={2} /> : k}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-6">
          <div className="text-[11px] uppercase tracking-widest text-gray-400 font-bold mb-8">
            Press &amp; hold to approve
          </div>
          <button
            data-testid="biometric-btn"
            onMouseDown={startBio}
            onMouseUp={cancelBio}
            onMouseLeave={cancelBio}
            onTouchStart={startBio}
            onTouchEnd={cancelBio}
            className="relative w-40 h-40 rounded-full flex items-center justify-center transition-all active:scale-95"
            style={{
              background:
                bioState === "approved"
                  ? "#059669"
                  : bioState === "scanning"
                  ? "#111827"
                  : "#F3F4F6",
            }}
          >
            {bioState === "scanning" && (
              <span className="absolute inset-0 rounded-full animate-ping bg-gray-900/20" />
            )}
            <Fingerprint
              className={
                "w-20 h-20 transition-colors " +
                (bioState === "idle" ? "text-gray-900" : "text-white")
              }
              strokeWidth={1.6}
            />
          </button>
          <div className="mt-8 text-[13px] font-semibold text-gray-700">
            {bioState === "idle" && "Touch sensor"}
            {bioState === "scanning" && "Scanning..."}
            {bioState === "approved" && "Approved"}
          </div>
          <PartnerBadge partner={partner.name} variant="pill" className="mt-6" />
        </div>
      )}
    </motion.div>
  );
};

export default AuthScreen;
